// @flow

import * as React from 'react';

import type { Site } from 'site/types';
import allSites from 'site/allSites';
import currentSite from 'site/currentSite';
import impossible from 'common/types/impossible';

import styles from './SearchInstructions.module.scss';

export type AlternativeSite = {|
  site_key: string,
  site_url: string
|};

export type Props = {|
  resultsFound: boolean,
  alternativeSite: AlternativeSite | null
|};

type Variant =
  | {| type: 'RESULTS_FOUND' |}
  | {| type: 'NO_RESULTS' |}
  | {|
      type: 'NO_RESULTS_ALTERNATIVE_SITE',
      alternativeSite: AlternativeSite,
      site: Site
    |};

function findSiteByKey(key: string): ?Site {
  return allSites().find(s => s.key === key);
}

function translatedSiteName(siteKey: string): ?string {
  const siteNames = __obj('search.search_instructions.site_names');
  return siteNames[siteKey] || null;
}

function getSiteName(site: Site): string {
  return translatedSiteName(site.key) || site.iso31661Alpha3 || site.key;
}

function resolveVariant(props: Props): Variant {
  if (props.resultsFound) {
    return { type: 'RESULTS_FOUND' };
  }

  const alternativeSite = props.alternativeSite;
  if (alternativeSite) {
    // Look up the site config for display name. If the key is unknown in
    // sites.json we fall back to the normal "no results" state.
    const site = findSiteByKey(alternativeSite.site_key);
    if (site) {
      return {
        type: 'NO_RESULTS_ALTERNATIVE_SITE',
        alternativeSite,
        site
      };
    }
  }

  return { type: 'NO_RESULTS' };
}

function AlternativeSiteHeader({ site }: {| site: Site |}): React.Node {
  const currentSiteName = translatedSiteName(currentSite().key);
  const alternativeSiteName = getSiteName(site);

  const headerText = currentSiteName
    ? I18n.interpolate(
        __(
          'search.search_instructions.header.results_not_found_alternative_site'
        ),
        {
          current_site_name: currentSiteName,
          alternative_site_name: alternativeSiteName
        }
      )
    : I18n.interpolate(
        __(
          'search.search_instructions.header.results_not_found_alternative_site_default'
        ),
        { alternative_site_name: alternativeSiteName }
      );

  return <h5>{headerText}</h5>;
}

function InstructionsContent({ variant }: {| variant: Variant |}): React.Node {
  switch (variant.type) {
    case 'RESULTS_FOUND':
      return (
        <>
          <h5>{__('search.search_instructions.header.results_found')}</h5>
          <h6 className={styles.subheader}>
            {__('search.search_instructions.subheader')}
          </h6>
        </>
      );
    case 'NO_RESULTS':
      return (
        <>
          <h5>{__('search.search_instructions.header.results_not_found')}</h5>
          <h6 className={styles.subheader}>
            {__('search.search_instructions.subheader')}
          </h6>
        </>
      );
    case 'NO_RESULTS_ALTERNATIVE_SITE':
      return (
        <>
          <AlternativeSiteHeader site={variant.site} />
          <a
            href={variant.alternativeSite.site_url}
            className={styles.alternativeSiteLink}
          >
            {I18n.interpolate(
              __('search.search_instructions.alternative_site_link'),
              {
                alternative_site_name: getSiteName(variant.site)
              }
            )}
          </a>
        </>
      );
    default:
      return impossible(variant.type);
  }
}

export default function SearchInstructions(props: Props): React.Node {
  const variant = resolveVariant(props);

  return (
    <section className={styles.container}>
      <hgroup>
        <InstructionsContent variant={variant} />
      </hgroup>
    </section>
  );
}
